/*
Компонент welcome popup.
Имеет 4 состояния (PopupStatus):
    - initial
    (
        Начальное состояние: отображается форма с выбором роли пользователя, если shouldWelcome=true
        При успешной отправке формы с выбором роли состояние переходит в saved

        Сама форма также имеет 3 статуса (FormStatus):
        - initial (начальное значение, форма не отправлялась)
        - submitting (форма в процессе отправки - кнопка блокируется)
        - hasError (результат fetch запроса вернул статус не 200 - выводится сообщение об ошибке)
    )
    - saved
    (
        Возникает при успешной отправке формы.
        Заменяет содержимое попапа с формы выбора роли на контентный попап,
        соответствующий выбранной роли из папки role-popups + сообщение о необходимости
        подтвердить email (не отображается, если пользователь выбрал роль oldtimer)

        Данные формы сохраняются в localstorage, перейти можно в состояние minified
    )
    - minified
    (
        Возникает, если закрыть контентный попап. Отображается минифицированный попап.
        Если пользователь выбрал роль oldtimer, минифицированный попап не отображается.

        При клике на минифицированный попап, состояние меняется на saved
        (снова открывается контентный попап).

        При закрытии минифицированного попапа состояние меняется на closed
        и больше не может быть изменено
    )
    - closed
    (
        Попап закрыт окончательно и больше не будет отображен ни в каком виде.
        Изменен быть не может.
    )
*/

import devPopup from "./role-popups/dev-popup";
import proxyPopup from "./role-popups/proxy-popup";
import nondevPopup from "./role-popups/nondev-popup";
import oldtimerPopup from "./role-popups/oldtimer-popup";
import basePopup from "../base-popup";

import cookies from "../../../cookies";
import storage from "../../../storage";
import urls from "../../../urls";
import ApiRequest from "../../../api-request";
import analytics from "../../../marketing/analytics";
import BaseRadio from "../../../components/base-radio-btn";

const { settings } = window.DA;
const serviceUrl = urls.api.demoProxy.getUrl();
const token = settings.API_KEY;
const CSRF_TOKEN = cookies.get("csrftoken");

const template = /* html */`
<transition :name="bigPopupAnimationName">
    <base-popup
        v-if="shouldShowBigPopup"
        :xl="true"
        :variant="popupIsClosable
                ? 'closable-fullscreen-mobile'
                : 'non-closable-fullscreen-mobile'"
        class="welcome"
        @closed-popup="minifyPopup"
    >
        <transition name="reveal">
            <div
                class="flex-column items-start gap-1"
                v-if="status == PopupStatus.INITIAL"
                data-test="welcome-role-form"
            >
                <h3>Какая помощь пригодится в настройке «Дадаты»?</h3>
                <base-radio
                    v-for="(roleText, role) in roles"
                    :key="role"
                    :value="role"
                    v-model="chosenRole"
                    :name="'role'"
                    :data-test="'welcome-role-radio-' + role"
                >
                    [[ roleText ]]
                </base-radio>
                <button
                    @click="saveUserRole"
                    :disabled="formBtnDisabled"
                    class="button-clean button-clean--red mt-0_5 hoverable"
                    data-test="welcome-role-form-btn"
                >
                    Закончить регистрацию
                </button>
                <transition name="slide-in">
                    <span
                        v-if="formStatus === FormStatus.HAS_ERROR"
                        class="error"
                    >
                        Не удалось обработать запрос
                    </span>
                </transition>
            </div>
            <div
                v-else
                class="flex-column items-start gap-1_5"
                :data-test="'welcome-role-content-' + chosenRole"
            >
                <div
                    class="accent-box accent-box--small"
                    v-if="!user.email_confirmed && chosenRole !== 'oldtimer'"
                    data-test="welcome-role-content-email-remainder"
                >
                    Первым делом подтвердите емейл, иначе «Дадата» не будет работать. 
                    Достаточно перейти по ссылке в письме, которое мы отправили 
                    на [[ user.username ]]. Если письмо не приходит, проверьте спам. 
                </div>
                <component
                    :is='contentPopupComponent'
                    :show-contacts="!(showDemoBlock || showDemoBlockSaved)"
                    @click="reachGoalOnLinkClick"
                >
                </component>
                <div
                    data-test="demo"
                    v-if="showDemoBlock || showDemoBlockSaved"
                    class="flex-row-sm flex-column gap-0_5 pt-1_5 demo"
                >
                    <div>
                        А еще можно созвониться с «Дадатой».
                        Хотите, проведем демо, расскажем о технических деталях
                        или просто познакомимся?
                        <a
                            href="https://calendly.com/elzax/30min"
                            target="_blank"
                        >Выбрать время</a>
                        <i class="fa fa-external-link pl-0_25 demo-icon" />
                    </div>
                    <img
                        src="/static/i/lamp.svg"
                        alt="Cозвониться с «Дадатой»"
                        class="demo-img"
                    >
                </div>
                <button
                    class="button-clean button-clean--bordered hoverable"
                    @click="minifyPopup"
                    data-test="welcome-minify-btn"
                >
                    Понятно
                </button>
            </div>
        </transition>
    </base-popup>
    <div
        v-else-if="showMinifiedPopup"
        class="default-modal default-modal--fixed welcome-modal"
        @click="revealPopup"
        data-test="welcome-minified"
    >
        <a href="#close" class="modal__close modal__close--small" @click.stop="closePopup">×</a>
        <span class="default-modal__title px-1">Как настроить «Дадату»</span>
    </div>
</transition>
`;

export default {
    template,
    props: { shouldWelcome: Boolean },
    setup() {
        const PopupStatus = {
            INITIAL: "initial",
            SAVED: "saved",
            MINIFIED: "minified",
            CLOSED: "closed",
        };
        const FormStatus = {
            INITIAL: "initial",
            SUBMITTING: "submitting",
            HAS_ERROR: "hasError",
        };
        return {
            PopupStatus,
            FormStatus,
        };
    },
    data() {
        return {
            chosenRole: "",
            companyData: null,
            showDemoBlockSaved: null,
            roles: {
                dev: "Дайте документацию, я разработчик",
                nondev: "Объясните на пальцах, я не разработчик",
                proxy: "Сейчас помощь не актуальна, настраивать будет другой человек",
                oldtimer: "Я старожил «Дадаты», все знаю и так",
            },
            status: this.PopupStatus.CLOSED,
            formStatus: this.FormStatus.INITIAL,
            user: window.DA.user,
            isMobile: false,
            storageName: `welcomePopup_${window.DA.user.id || ""}`,
        };
    },
    mounted() {
        // Изначально компонент создается в closed состоянии, чтобы избежать "моргания",
        // затем, если есть данные в localstorage, они устанавливаются в data
        if (storage.has(this.storageName)) {
            this.chosenRole = storage.get(this.storageName).role;
            this.status = storage.get(this.storageName).status;
            this.showDemoBlockSaved = storage.get(this.storageName).showDemo;
        }

        // Отслеживается ширина окна, чтобы использовать разные анимации и сделать,
        // чтобы контентные попапы могли быть закрыты крестиком только на моб.устройствах
        const mql = window.matchMedia("(max-width: 972px)");
        this.isMobile = mql.matches;

        mql.addEventListener("change", e => {
            this.isMobile = e.matches;
        });

        // При переходе между страницами принудительно меняем состояние на minified,
        // если попап открыт и пройдена стадия выбора роли
        window.addEventListener("beforeunload", this.minifyOnPageChange);
    },
    beforeUnmount() {
        window.removeEventListener("beforeunload", this.minifyOnPageChange);
    },
    methods: {
        async saveUserRole() {
            if (!this.chosenRole) return;
            try {
                this.formStatus = this.FormStatus.SUBMITTING;

                const options = {
                    method: "POST",
                    body: new URLSearchParams({ whoami: this.chosenRole })
                        .toString(),
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Accept: "application/json",
                        "X-CSRFToken": CSRF_TOKEN,
                    },
                };

                const response = await fetch("/profile/welcome/", options);

                if (response.status !== 200) {
                    this.formStatus = this.FormStatus.HAS_ERROR;
                    return;
                }

                if (this.chosenRole !== "oldtimer") {
                    const api = new ApiRequest(`${serviceUrl}/findByEmail/company`, {
                        token,
                        csrf: CSRF_TOKEN,
                    });
                    const result = await api.send({ query: this.user.username }, false);
                    this.companyData = result.suggestions
                        ? (result.suggestions[0] || {}).data
                        : null;
                }

                storage.set(this.storageName, {
                    role: this.chosenRole,
                    status: this.PopupStatus.SAVED,
                    showDemo: this.showDemoBlock,
                });

                analytics.reachGoal(`${this.goalBaseName}_CHOOSE`);

                this.status = this.PopupStatus.SAVED;
            } catch (e) {
                this.formStatus = this.FormStatus.HAS_ERROR;
            }
        },
        minifyPopup() {
            const currentData = storage.get(this.storageName) || {};
            storage.set(this.storageName, {
                ...currentData,
                status: this.PopupStatus.MINIFIED,
            });
            this.status = this.PopupStatus.MINIFIED;
        },
        closePopup() {
            const currentData = storage.get(this.storageName) || {};
            storage.set(this.storageName, {
                ...currentData,
                status: this.PopupStatus.CLOSED,
            });
            this.status = this.PopupStatus.CLOSED;
            analytics.reachGoal(`${this.goalBaseName}_CLOSE`);
        },
        revealPopup() {
            this.status = this.PopupStatus.SAVED;
        },
        reachGoalOnLinkClick(e) {
            if (!e.target.href || e.target.className.includes("popup__close")) return;
            analytics.reachGoal(
                `${this.goalBaseName}_CLICK`,
                { "onboarding-clicked-link": e.target.href },
            );
        },
        minifyOnPageChange() {
            if (this.status !== this.PopupStatus.CLOSED && storage.has(this.storageName)) {
                this.minifyPopup();
            }
        },
    },
    provide() {
        return { userEmail: this.user.username };
    },
    computed: {
        // Анимация для попапа в не минифицированном состоянии.
        // Выглядит по разному для начального попапа с формой и контентного попапа
        // в десктопе и на моб.устройствах
        // Скрытие попапа для oldtimer также отличается от скрытия остальных ролевых попапов
        // т.к. он не минифицируется, а сразу закрывается
        bigPopupAnimationName() {
            if (
                (
                    this.shouldWelcome &&
                    ![this.PopupStatus.SAVED, this.PopupStatus.MINIFIED].includes(this.status)
                ) ||
                (
                    this.chosenRole === "oldtimer"
                )
            ) return "fade";
            return this.isMobile ? "minify-bottom" : "minify-right";
        },
        // Определяет, какой контентный попап показать в зависимости от выбранной роли
        contentPopupComponent() {
            return `${this.chosenRole}-popup`;
        },
        // Определяет, должен ли закрываться попап по клику на крестик на моб.устройствах
        // (закрывается, только если это контентный попап)
        popupIsClosable() {
            return this.status === this.PopupStatus.SAVED;
        },
        goalBaseName() {
            return `WELCOME_POPUP_ROLE_${this.chosenRole.toUpperCase()}`;
        },
        formBtnDisabled() {
            return !this.chosenRole || this.formStatus === this.FormStatus.SUBMITTING;
        },
        showMinifiedPopup() {
            return this.status === this.PopupStatus.MINIFIED && this.chosenRole !== "oldtimer";
        },
        shouldShowBigPopup() {
            return this.status === this.PopupStatus.INITIAL ||
            this.status === this.PopupStatus.SAVED;
        },
        showDemoBlock() {
            const isEmailCorporate = ["ROLE", "CORPORATE"].includes(this.companyData?.email?.type);
            return this.chosenRole !== "oldtimer" && isEmailCorporate;
        },
    },
    // Актуальное значение shouldWelcome prop определяется после маунтинга родительского компонента
    // Поэтому отслеживается его изменение и если оно true (и при этом нет сохраненных
    // ранее данных, а пользователь залогинен), то состояние устанавливается в initial
    // (будет показана форма выбора роли)
    watch: {
        shouldWelcome(newVal) {
            if (!newVal || storage.has(this.storageName) || !window.DA.user.id) return;
            this.status = this.PopupStatus.INITIAL;
        },
    },
    components: {
        devPopup,
        proxyPopup,
        nondevPopup,
        oldtimerPopup,
        basePopup,
        BaseRadio,
    },
};
